import { Constants, ApiResultModel, HttpMethod } from '@truckdown/systems';
import { UserContext } from '@truckdown/systems/Context/UserContext';
import context, { SystemConstants } from '@truckdown/components-systems';

import { defineComponent, ref, Ref, onMounted, onUnmounted } from 'vue';

import progressTracker from './ProgressTracker/ProgressTracker.vue';
import sideBar from './SideBar/SideBar.vue';
import stepSummary from './StepSummary/StepSummary.vue';
import stepTitle from './StepTitle/StepTitle.vue';

import teamSetup from './TeamSetup/TeamSetup.vue';
import favoritesSetup from './FavoritesSetup/FavoritesSetup.vue';
import vendorsSetup from './VendorsSetup/VendorsSetup.vue';
import activate from './Activate/Activate.vue';

interface IProps {
    section?: string;
}

interface IData {
    currentSection: Ref<'team' | 'favorites' | 'vendors' | 'setup'>
}

const isSectionValid = function (section: string) {
    return section == 'team'
        || section == 'favorites'
        || section == 'vendors'
        || section == 'setup';
}

const getCurrentSection = function (props: IProps) {
    const setting = context.settings['fleet_onboarding'];
    if (!setting) {
        return 'team';
    }

    if (props.section && isSectionValid(props.section)) {
        if (props.section == 'team' || props.section == 'favorites') {
            return props.section;
        }
        if (props.section == 'vendors' && (setting == 'favorites' || setting == 'vendors')) {
            return props.section;
        }
        if (props.section == 'setup' && setting == 'vendors') {
            return props.section;
        }
    }

    if (setting && setting == 'team') {
        return 'favorites';
    }
    if (setting && setting == 'favorites') {
        return 'vendors';
    }
    if (setting && setting == 'vendors') {
        return 'setup';
    }

    return 'team';
}

const getData = function (props: IProps) {
    return {
        currentSection: ref<string>(getCurrentSection(props))
    } as IData;
}

const refreshProgress = async function (data: IData) {
    let manager = context.apiManager;
    let req = manager.createRequest({
        service: 'main',
        method: HttpMethod.GET,
        path: '/systems/context/refresh-client',
    });
    var res = await manager.get<any>(req);
    if (res) {
        let val = JSON.stringify(res);
        let newContext = new UserContext(context.antiforgeryToken, val);

        context.settings = newContext.settings;
        context.claims = newContext.claims;
        context.user = newContext.user;
        context.authToken = newContext.authToken;
        refreshContext(data);
        context.events.emit("refresh_onboarding_progress");
    } else {
        console.log('Error refreshing user context.');
    }
}

const completeCurrent = async function (data: IData) {
    try {
        let manager = context.apiManager;
        let request = manager.createRequest({
            service: "main",
            path: "/accounts/fleet-onboarding/setting/" + data.currentSection.value,
            method: HttpMethod.POST
        });
        var res = await manager.get<ApiResultModel>(request);
        if (res.ok) {
            await refreshProgress(data);
        } else {
            console.log('Error completing current state', res);
        }
    }
    catch (err) {
        console.log(err);
    }
}

const getMethods = function (data: IData) {
    return {
        changeSection: function (section: string) {
            console.log('receiving event change-section');
            if (changeSection(data, section)) {
                updateQueryString(section);
            }
        },
        completeStep: async function () {
            console.log('Complete step event fired');
            //note: setup final function is handled in the setup component due to validation
            if (data.currentSection.value != 'setup') {
                await completeCurrent(data);
            } else {
                context.events.emit('fleet-setup-activate');
            }
        }
    }
}

const changeSection = function (data: IData, section: string) {
    var section = getCurrentSection({ section: section });
    if (section != data.currentSection.value) {
        data.currentSection.value = section as any;
        return true;
    }
    return false;
}

const updateQueryString = function (section: string) {
    const url = new URL(window.location.href);
    url.searchParams.set('section', section);
    window.history.pushState({}, '', url);
}

const replaceQueryString = function (section: string) {
    const url = new URL(window.location.href);
    url.searchParams.set('section', section);
    window.history.replaceState({}, '', url);
}

const refreshContext = function (data: IData) {
    data.currentSection.value = getCurrentSection({});
    replaceQueryString(data.currentSection.value);
}

export default defineComponent({
    props: {
        section: String
    },
    name: "fleet-setup",
    emits: [],
    components: {
        'fleet-setup-progress-tracker': progressTracker,
        'fleet-setup-side-bar': sideBar,
        'fleet-setup-step-summary': stepSummary,
        'fleet-setup-step-title': stepTitle,
        'fleet-setup-team-setup': teamSetup,
        'fleet-setup-favorites-setup': favoritesSetup,
        'fleet-setup-vendors-setup': vendorsSetup,
        'fleet-setup-activate': activate
    },
    setup: function (props: IProps, ctx: any) {
        const data = getData(props);
        const methods = getMethods(data);

        const refreshContextEvent = function () {
            refreshContext(data);
        }

        const handlePopState = function () {
            const url = new URL(window.location.href);
            const section = url.searchParams.get('section');
            if (!section) return;
            changeSection(data, section);
        }

        onMounted(() => {
            context.events.on(SystemConstants.Events.RefreshOnboardingProgress, refreshContextEvent);
            window.addEventListener('popstate', handlePopState);
            replaceQueryString(data.currentSection.value);
        });

        onUnmounted(() => {
            context.events.off(SystemConstants.Events.RefreshOnboardingProgress, refreshContextEvent);
            window.removeEventListener('popstate', handlePopState);
        });

        return {
            ...data,
            ...methods
        };
    }
});
